import { type KeyboardEvent, useMemo, useState } from "react";

import { Button } from "@/components/_atoms/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_atoms/command";
import { Dialog, DialogContent } from "@/components/_atoms/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { IconCheckLine } from "@/components/_icons/IconCheckLine";
import { Link } from "@/components/_molecules/navigation-controls/link";
import { useFeatureFlagCached } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-cached";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { CurrentUser } from "@/domain/user/models/current-user-model";
import { AvatarWorkspace } from "@/domain/workspace/components/avatar-workspace";
import { WorkspaceSelect } from "@/domain/workspace/components/workspace-select";
import { useCanManageWorkspace } from "@/domain/workspace/hooks/use-can-manage-workspace";
import { makeWorkspaceSettingsUrl } from "@/domain/workspace/lib/make-workspace-url";
import { Workspace } from "@/domain/workspace/models/workspace-model";

interface WorkspaceRowContentProps {
  workspace: Workspace;
  isSelected: boolean;
}

const WorkspaceRowContent = ({ workspace, isSelected }: WorkspaceRowContentProps) => (
  <span className="flex w-full select-none flex-row items-center gap-2 text-left text-xs">
    <AvatarWorkspace className="text-2xs h-6 w-6 shrink-0" workspace={workspace} />
    <span className="min-w-0 flex-1 truncate">{workspace.name}</span>
    {isSelected && <IconCheckLine className="shrink-0" />}
  </span>
);

interface WorkspaceSwitcherDropdownProps {
  workspace: Workspace;
  user: CurrentUser;
}

export const WorkspaceSwitcherDropdown = ({ workspace, user }: WorkspaceSwitcherDropdownProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useDomainTranslation("common");
  const { canManageWorkspace } = useCanManageWorkspace({ workspaceId: workspace.id });
  const { isEnabled: isWorkspaceSearchEnabled } = useFeatureFlagCached({
    workspaceId: workspace.id,
    id: "enable-workspace-search-bar",
  });

  const workspaceMembershipList = useMemo(() => user?.workspaceMembershipList ?? [], [user]);

  const activeWorkspaceMembershipList = useMemo(
    () => workspaceMembershipList.filter((workspaceMembership) => workspaceMembership.status === "ACTIVE"),
    [workspaceMembershipList],
  );

  const hasHiddenWorkspaces = workspaceMembershipList.some((workspaceMembership) => workspaceMembership.status === "LEFT");
  const showWorkspaceSearch = isWorkspaceSearchEnabled && activeWorkspaceMembershipList.length > 1;

  function handleSelectWorkspace(workspaceUrl?: string) {
    if (!workspaceUrl) {
      return;
    }
    window.location.replace(new URL(`${window.location.origin}/${workspaceUrl}`));
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const isCharacterKey = event.key.length === 1;
    const hasModifier = event.ctrlKey || event.altKey || event.metaKey;
    if (isCharacterKey && !hasModifier) {
      event.stopPropagation();
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="items-left flex h-9 flex-initial select-none flex-row justify-start gap-2 px-2 text-left text-xs hover:bg-foreground/3"
            variant="ghost"
            size="xs"
            style={{ maxWidth: "150px" }}
          >
            <AvatarWorkspace workspace={workspace} className="h-6 w-6" />
            <span className="max-w-min overflow-hidden text-ellipsis">{workspace.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="flex max-h-[80vh] w-64 flex-col overflow-hidden p-0"
          onCloseAutoFocus={(event) => {
            if (showWorkspaceSearch) {
              event.preventDefault();
            }
          }}
        >
          {user && (
            <DropdownMenuItem asChild disabled className="px-2 py-1.5">
              <Link to="" target="_blank" className="block truncate">
                {user.email}
              </Link>
            </DropdownMenuItem>
          )}
          {showWorkspaceSearch ? (
            <Command onKeyDown={handleSearchKeyDown}>
              <CommandInput
                placeholder={t("sidebar.searchWorkspaces")}
                aria-label={t("sidebar.searchWorkspaces")}
                autoFocus
              />
              <CommandList className="max-h-none overflow-y-auto">
                <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
                  {t("search.noResultsShort")}
                </CommandEmpty>
                <CommandGroup>
                  {activeWorkspaceMembershipList.map((workspaceMembership) => {
                    if (!workspaceMembership.workspace) {
                      return null;
                    }
                    return (
                      <CommandItem
                        key={workspaceMembership.workspaceId}
                        value={workspaceMembership.workspace.name}
                        onSelect={() => handleSelectWorkspace(workspaceMembership.workspace?.workspaceUrl)}
                        className="cursor-pointer"
                      >
                        <WorkspaceRowContent
                          workspace={workspaceMembership.workspace}
                          isSelected={workspaceMembership.workspaceId === workspace.id}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <div className="overflow-y-auto py-1">
              {activeWorkspaceMembershipList.map((workspaceMembership) => {
                if (!workspaceMembership.workspace) {
                  return null;
                }
                return (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    key={workspaceMembership.workspaceId}
                    onClick={() => handleSelectWorkspace(workspaceMembership.workspace?.workspaceUrl)}
                  >
                    <WorkspaceRowContent
                      workspace={workspaceMembership.workspace}
                      isSelected={workspaceMembership.workspaceId === workspace.id}
                    />
                  </DropdownMenuItem>
                );
              })}
            </div>
          )}
          {hasHiddenWorkspaces && (
            <DropdownMenuItem className="cursor-pointer" onClick={() => setIsDialogOpen(true)}>
              <span className="cursor-pointer text-xs">{t("sidebar.showAll")}</span>
            </DropdownMenuItem>
          )}
          {canManageWorkspace && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={makeWorkspaceSettingsUrl({ workspace, path: "general" })}>
                  {t("sidebar.workspaceSettings")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={makeWorkspaceSettingsUrl({ workspace, path: "members" })}>{t("sidebar.manageMembers")}</Link>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {hasHiddenWorkspaces && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="h-screen w-screen max-w-none">
            <WorkspaceSelect />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
